import { useMemo } from 'react';
import { Plus, Minus, ArrowRight, Calendar, Bed, Dumbbell } from 'lucide-react';
import { diffSeasons, diffIsEmpty, type DayDiff, type ExerciseDiff, type SessionDiff } from '../lib/seasonDiff';
import type { Season } from '../types/workout';
import { formatWorkoutDate } from '../lib/utils';
import { cn } from '../lib/utils';

interface DiffViewerProps {
  before: Season | null;
  after: Season | null;
  /** Optional title shown above the summary header. */
  title?: string;
}

function ChangeBadge({ kind }: { kind: 'added' | 'removed' | 'modified' | 'rest_to_workout' | 'workout_to_rest' | 'goal_changed' | 'category_changed' | 'sessions_changed' | 'exercises_changed' }) {
  const styles: Record<string, string> = {
    added: 'bg-green-100 text-green-700 border-green-300',
    removed: 'bg-red-100 text-red-700 border-red-300',
    modified: 'bg-amber-100 text-amber-700 border-amber-300',
    rest_to_workout: 'bg-blue-100 text-blue-700 border-blue-300',
    workout_to_rest: 'bg-blue-100 text-blue-700 border-blue-300',
    goal_changed: 'bg-amber-100 text-amber-700 border-amber-300',
    category_changed: 'bg-amber-100 text-amber-700 border-amber-300',
    sessions_changed: 'bg-amber-100 text-amber-700 border-amber-300',
    exercises_changed: 'bg-amber-100 text-amber-700 border-amber-300',
  };
  const labels: Record<string, string> = {
    added: 'Added',
    removed: 'Removed',
    modified: 'Modified',
    rest_to_workout: 'Rest → Workout',
    workout_to_rest: 'Workout → Rest',
    goal_changed: 'Goal changed',
    category_changed: 'Category changed',
    sessions_changed: 'Sessions changed',
    exercises_changed: 'Exercises changed',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
        styles[kind] ?? 'bg-muted text-muted-foreground border-border',
      )}
    >
      {labels[kind] ?? kind}
    </span>
  );
}

function fmtExercise(ex: { exercise: string; sets: number; reps: string; weight: number }): string {
  const parts = [ex.exercise || '(unnamed)'];
  if (ex.sets) parts.push(`${ex.sets}x${ex.reps || '?'}`);
  if (ex.weight) parts.push(`${ex.weight} lbs`);
  return parts.join(' · ');
}

function ExerciseDiffRow({ ed }: { ed: ExerciseDiff }) {
  if (ed.kind === 'added') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50/70 px-2 py-1.5 text-xs">
        <Plus className="size-3.5 text-green-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-green-900">{fmtExercise(ed.exercise)}</div>
          <div className="text-[10px] text-green-700/80">Session {ed.sessionIndex + 1} · slot {ed.exerciseIndex + 1}</div>
        </div>
      </div>
    );
  }
  if (ed.kind === 'removed') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50/70 px-2 py-1.5 text-xs">
        <Minus className="size-3.5 text-red-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-red-900 line-through">{fmtExercise(ed.exercise)}</div>
          <div className="text-[10px] text-red-700/80">Session {ed.sessionIndex + 1} · slot {ed.exerciseIndex + 1}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50/70 px-2 py-1.5 text-xs">
      <div className="flex items-center gap-1.5">
        <Dumbbell className="size-3.5 text-amber-700 shrink-0" />
        <div className="font-medium text-amber-900">{ed.before.exercise || '(unnamed)'}</div>
        <span className="text-[10px] text-amber-700/80">· Session {ed.sessionIndex + 1}</span>
      </div>
      <div className="mt-1 grid gap-0.5 pl-4">
        {ed.changes.map((c, i) => (
          <div key={i} className="flex items-center gap-1.5 text-[11px]">
            <span className="font-medium text-amber-700/90 capitalize w-12">{c.field}:</span>
            <span className="line-through text-red-700/70">{String(c.before) || '—'}</span>
            <ArrowRight className="size-3 text-muted-foreground" />
            <span className="font-semibold text-green-700">{String(c.after) || '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SessionDiffRow({ sd }: { sd: SessionDiff }) {
  if (sd.kind === 'session_added') {
    return (
      <div className="flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50/40 px-2 py-1 text-[11px] text-green-800">
        <Plus className="size-3 shrink-0" />
        <span className="font-medium">Session added:</span>
        <span>{sd.session.name || sd.session.category || `Session ${sd.sessionIndex + 1}`}</span>
      </div>
    );
  }
  if (sd.kind === 'session_removed') {
    return (
      <div className="flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50/40 px-2 py-1 text-[11px] text-red-800">
        <Minus className="size-3 shrink-0" />
        <span className="font-medium">Session removed:</span>
        <span className="line-through">{sd.session.name || sd.session.category || `Session ${sd.sessionIndex + 1}`}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50/40 px-2 py-1 text-[11px] text-amber-800">
      <ArrowRight className="size-3 shrink-0" />
      <span className="font-medium">Session renamed:</span>
      <span className="line-through text-red-700/70">{sd.before || '(empty)'}</span>
      <ArrowRight className="size-3" />
      <span className="font-semibold text-green-700">{sd.after || '(empty)'}</span>
    </div>
  );
}

function DayDiffCard({ dd }: { dd: DayDiff }) {
  return (
    <div className="rounded-lg border bg-card p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="size-4 text-muted-foreground" />
          <span className="font-semibold text-sm">{formatWorkoutDate(dd.date)}</span>
          <span className="text-[10px] text-muted-foreground tabular-nums">{dd.date}</span>
        </div>
        <ChangeBadge kind={dd.status === 'unchanged' ? 'modified' : (dd.status as any)} />
      </div>

      {dd.status === 'rest_to_workout' && (
        <div className="text-xs text-blue-700 flex items-center gap-1.5">
          <Bed className="size-3.5" /> was a rest day · now a workout day
        </div>
      )}
      {dd.status === 'workout_to_rest' && (
        <div className="text-xs text-blue-700 flex items-center gap-1.5">
          <Bed className="size-3.5" /> was a workout day · now a rest day
        </div>
      )}

      {(dd.goalBefore !== undefined || dd.goalAfter !== undefined) && dd.goalBefore !== dd.goalAfter && (
        <div className="text-xs">
          <span className="font-medium text-muted-foreground">Goal: </span>
          <span className="line-through text-red-700/70">{dd.goalBefore || '(empty)'}</span>
          <ArrowRight className="inline size-3 mx-1 text-muted-foreground" />
          <span className="font-medium text-green-700">{dd.goalAfter || '(empty)'}</span>
        </div>
      )}

      {dd.categoryBefore !== dd.categoryAfter && (dd.categoryBefore || dd.categoryAfter) && (
        <div className="text-xs">
          <span className="font-medium text-muted-foreground">Category: </span>
          <span className="line-through text-red-700/70">{dd.categoryBefore || '(none)'}</span>
          <ArrowRight className="inline size-3 mx-1 text-muted-foreground" />
          <span className="font-medium text-green-700">{dd.categoryAfter || '(none)'}</span>
        </div>
      )}

      {dd.sessionDiffs.length > 0 && (
        <div className="space-y-1">
          {dd.sessionDiffs.map((sd, i) => (
            <SessionDiffRow key={i} sd={sd} />
          ))}
        </div>
      )}

      {dd.exerciseDiffs.length > 0 && (
        <div className="space-y-1.5 mt-1">
          {dd.exerciseDiffs.map((ed, i) => (
            <ExerciseDiffRow key={i} ed={ed} />
          ))}
        </div>
      )}
    </div>
  );
}

export function DiffViewer({ before, after, title }: DiffViewerProps) {
  const diff = useMemo(() => diffSeasons(before, after), [before, after]);

  if (diffIsEmpty(diff)) {
    return (
      <div className="rounded-lg border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
        No changes proposed.
      </div>
    );
  }

  const s = diff.summary;
  return (
    <div className="space-y-3">
      {title && <h3 className="font-semibold text-sm">{title}</h3>}

      <div className="rounded-lg border bg-card p-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {s.weeksAdded > 0 && <span className="text-green-700"><strong>+{s.weeksAdded}</strong> week{s.weeksAdded === 1 ? '' : 's'}</span>}
        {s.weeksRemoved > 0 && <span className="text-red-700"><strong>−{s.weeksRemoved}</strong> week{s.weeksRemoved === 1 ? '' : 's'}</span>}
        {s.daysAdded > 0 && <span className="text-green-700"><strong>+{s.daysAdded}</strong> day{s.daysAdded === 1 ? '' : 's'}</span>}
        {s.daysRemoved > 0 && <span className="text-red-700"><strong>−{s.daysRemoved}</strong> day{s.daysRemoved === 1 ? '' : 's'}</span>}
        {s.daysModified > 0 && <span className="text-amber-700"><strong>{s.daysModified}</strong> day{s.daysModified === 1 ? '' : 's'} modified</span>}
        {s.exercisesAdded > 0 && <span className="text-green-700"><strong>+{s.exercisesAdded}</strong> exercise{s.exercisesAdded === 1 ? '' : 's'}</span>}
        {s.exercisesRemoved > 0 && <span className="text-red-700"><strong>−{s.exercisesRemoved}</strong> exercise{s.exercisesRemoved === 1 ? '' : 's'}</span>}
        {s.exercisesModified > 0 && <span className="text-amber-700"><strong>{s.exercisesModified}</strong> exercise{s.exercisesModified === 1 ? '' : 's'} modified</span>}
        {s.sessionsAdded > 0 && <span className="text-green-700"><strong>+{s.sessionsAdded}</strong> session{s.sessionsAdded === 1 ? '' : 's'}</span>}
        {s.sessionsRemoved > 0 && <span className="text-red-700"><strong>−{s.sessionsRemoved}</strong> session{s.sessionsRemoved === 1 ? '' : 's'}</span>}
      </div>

      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
        {diff.changedDays.map((dd) => (
          <DayDiffCard key={dd.date} dd={dd} />
        ))}
      </div>
    </div>
  );
}
